import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileText, Copy, CheckCircle2, Archive, Trash2, GitBranch } from "lucide-react";
import { inr } from "@/lib/utils-format";

const STATUS_TONES = {
  draft: "bg-slate-100 text-slate-700",
  approved: "bg-emerald-100 text-emerald-800",
  archived: "bg-red-50 text-red-600",
};

export function MfgBomTab() {
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [rows, setRows] = useState([]);
  const [productFilter, setProductFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRefs = async () => {
    try {
      const [p, b, i] = await Promise.all([
        api.get("/products"), api.get("/branches"), api.get("/ingredients"),
      ]);
      setProducts(p.data); setBranches(b.data); setIngredients(i.data);
    } catch { /* */ }
  };
  useEffect(() => { loadRefs(); }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (productFilter) params.set("product_id", productFilter);
    if (branchFilter) params.set("branch_id", branchFilter);
    if (statusFilter) params.set("status", statusFilter);
    try {
      const q = params.toString() ? `?${params.toString()}` : "";
      const r = await api.get(`/mfg/boms${q}`);
      setRows(r.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productFilter, branchFilter, statusFilter]);

  const branchName = (id) => id ? (branches.find((b) => b.id === id) || {}).name || id : "All Factories (default)";
  const ingName = (id) => (ingredients.find((i) => i.id === id) || {}).name || id;
  const ingCost = (id) => Number((ingredients.find((i) => i.id === id) || {}).cost_per_unit || 0);

  const openNew = () => {
    setForm({
      product_id: "",
      branch_id: null,
      yield_qty: 1,
      yield_unit: "units",
      ingredients: [],
      notes: "",
    });
    setOpen(true);
  };

  const openEdit = async (r) => {
    // Fetch fresh with details
    try {
      const detail = await api.get(`/mfg/boms/${r.id}`);
      setForm(detail.data);
      setOpen(true);
    } catch { /* */ }
  };

  const computedCost = useMemo(() => {
    if (!form) return { total: 0, per_unit: 0 };
    let t = 0;
    for (const i of form.ingredients || []) {
      const cp = ingCost(i.ingredient_id);
      t += Number(i.quantity || 0) * cp;
    }
    return { total: t, per_unit: form.yield_qty > 0 ? t / Number(form.yield_qty) : 0 };
    // eslint-disable-next-line
  }, [form, ingredients]);

  const save = async () => {
    if (!form.product_id) { toast.error("Product required"); return; }
    if (!form.ingredients?.length) { toast.error("Add at least 1 ingredient"); return; }
    setBusy(true);
    try {
      const payload = {
        product_id: form.product_id,
        branch_id: form.branch_id || null,
        yield_qty: Number(form.yield_qty || 1),
        yield_unit: form.yield_unit,
        ingredients: form.ingredients.map((i) => ({
          ingredient_id: i.ingredient_id,
          quantity: Number(i.quantity),
          unit: i.unit,
          notes: i.notes,
        })),
        notes: form.notes,
      };
      if (form.id) {
        const { product_id, branch_id, ...body } = payload;
        await api.patch(`/mfg/boms/${form.id}`, body);
      } else {
        await api.post("/mfg/boms", payload);
      }
      toast.success("BOM saved");
      setOpen(false); setForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doAction = async (r, action) => {
    try {
      const { data } = await api.post(`/mfg/boms/${r.id}/${action}`);
      toast.success(action === "approve" ? `BOM v${data.version} approved` : action === "clone" ? `Cloned as v${data.version} draft` : `Archived`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete draft BOM v${r.version} for ${r.product_name}?`)) return;
    try { await api.delete(`/mfg/boms/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addIngredient = () => {
    setForm({ ...form, ingredients: [...(form.ingredients || []), { ingredient_id: "", quantity: 0, unit: "" }] });
  };
  const removeIngredient = (idx) => {
    const next = form.ingredients.filter((_, i) => i !== idx);
    setForm({ ...form, ingredients: next });
  };
  const updateIngredient = (idx, patch) => {
    const next = form.ingredients.map((i, ii) => ii === idx ? { ...i, ...patch } : i);
    setForm({ ...form, ingredients: next });
  };

  return (
    <div className="pt-3 space-y-4" data-testid="mfg-bom-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Product</Label>
          <Select value={productFilter} onValueChange={(v) => setProductFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-60" data-testid="bom-product-filter"><SelectValue placeholder="All products" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72 overflow-y-auto">
              <SelectItem value="__all">All products</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Factory</Label>
          <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="bom-branch-filter"><SelectValue placeholder="All factories" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All factories</SelectItem>
              {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="bom-status-filter"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">Any status</SelectItem>
              {["draft", "approved", "archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="bg-[#C05621] text-white ml-auto self-end" onClick={openNew} data-testid="bom-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New BOM
        </Button>
      </div>

      {/* Table */}
      <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="bom-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Factory</th>
              <th className="text-center px-3 py-2">Ver</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Yield</th>
              <th className="text-right px-3 py-2">RM Cost</th>
              <th className="text-right px-3 py-2">Cost/unit</th>
              <th className="text-left px-3 py-2">Approved</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`bom-row-${r.id}`}>
                <td className="px-3 py-2"><div className="font-medium">{r.product_name}</div><div className="text-[10px] text-[#8B7355]">{r.product_sku}</div></td>
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2 text-center font-mono text-xs">v{r.version}</td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${STATUS_TONES[r.status]}`}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-right font-mono">{r.yield_qty} {r.yield_unit}</td>
                <td className="px-3 py-2 text-right font-mono">{inr(r.total_rm_cost || 0)}</td>
                <td className="px-3 py-2 text-right font-mono">{inr(r.cost_per_unit || 0)}</td>
                <td className="px-3 py-2 text-xs text-[#8B7355]">{r.approved_by_name || "—"}<div className="text-[10px]">{r.approved_at ? r.approved_at.slice(0, 10) : ""}</div></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`bom-edit-${r.id}`}>View</Button>
                  {r.status === "draft" && (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 ml-1" onClick={() => doAction(r, "approve")} data-testid={`bom-approve-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  {r.status === "approved" && (
                    <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 ml-1" onClick={() => doAction(r, "clone")} data-testid={`bom-clone-${r.id}`}>
                      <Copy className="w-3 h-3 mr-1" /> Clone
                    </Button>
                  )}
                  {r.status !== "archived" && r.status !== "draft" && (
                    <Button size="sm" variant="ghost" className="text-slate-500 ml-1" onClick={() => doAction(r, "archive")} data-testid={`bom-archive-${r.id}`}><Archive className="w-3.5 h-3.5" /></Button>
                  )}
                  {r.status === "draft" && (
                    <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r)} data-testid={`bom-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[#8B7355]">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No BOMs yet. Click <b>New BOM</b> to define one — you can create a default (all factories) or a per-factory override.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setForm(null); } }}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">
              {form?.id ? `Edit BOM v${form.version} — ${form.product_name} (${form.status})` : "New BOM"}
            </DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Product*</Label>
                  <Select value={form.product_id || ""} onValueChange={(v) => setForm({ ...form, product_id: v })} disabled={!!form.id}>
                    <SelectTrigger data-testid="bom-form-product"><SelectValue placeholder="Choose product" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72 overflow-y-auto">
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Factory scope</Label>
                  <Select value={form.branch_id || "__default"} onValueChange={(v) => setForm({ ...form, branch_id: v === "__default" ? null : v })} disabled={!!form.id}>
                    <SelectTrigger data-testid="bom-form-branch"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__default">Default (all factories)</SelectItem>
                      {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Yield qty*</Label>
                  <Input type="number" step="0.1" min="0" value={form.yield_qty} onChange={(e) => setForm({ ...form, yield_qty: e.target.value })} disabled={form.status && form.status !== "draft"} data-testid="bom-form-yield" />
                </div>
                <div>
                  <Label>Yield unit</Label>
                  <Input value={form.yield_unit || ""} onChange={(e) => setForm({ ...form, yield_unit: e.target.value })} disabled={form.status && form.status !== "draft"} placeholder="units / kg / pcs" />
                </div>
              </div>

              <div className="border border-[#EAE5D9] rounded-md p-3 bg-[#FDFBF7]">
                <div className="flex justify-between items-center mb-2">
                  <div className="uc-label !text-[#5C4033]">Ingredients</div>
                  {(!form.status || form.status === "draft") && (
                    <Button size="sm" variant="outline" onClick={addIngredient} data-testid="bom-add-ingredient">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  )}
                </div>
                {(form.ingredients || []).length === 0 && (
                  <div className="text-center text-xs text-[#8B7355] py-3">No ingredients yet.</div>
                )}
                {(form.ingredients || []).map((ing, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end mb-2" data-testid={`bom-ing-row-${idx}`}>
                    <div className="col-span-5">
                      <Label className="text-[10px]">Ingredient</Label>
                      <Select value={ing.ingredient_id || ""} onValueChange={(v) => {
                        const master = ingredients.find((x) => x.id === v);
                        updateIngredient(idx, { ingredient_id: v, unit: ing.unit || master?.unit });
                      }} disabled={form.status && form.status !== "draft"}>
                        <SelectTrigger data-testid={`bom-ing-select-${idx}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                        <SelectContent className="bg-white max-h-72 overflow-y-auto">
                          {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} {i.cost_per_unit ? `· ${inr(i.cost_per_unit)}/${i.unit}` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Quantity</Label>
                      <Input type="number" step="0.001" min="0" value={ing.quantity ?? 0} onChange={(e) => updateIngredient(idx, { quantity: Number(e.target.value) })} disabled={form.status && form.status !== "draft"} data-testid={`bom-ing-qty-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Unit</Label>
                      <Input value={ing.unit || ""} onChange={(e) => updateIngredient(idx, { unit: e.target.value })} disabled={form.status && form.status !== "draft"} />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <span className="text-xs font-mono">{inr(Number(ing.quantity || 0) * ingCost(ing.ingredient_id))}</span>
                      {(!form.status || form.status === "draft") && (
                        <button className="text-red-500 hover:text-red-700" onClick={() => removeIngredient(idx)}><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="border-t border-[#EAE5D9] pt-2 mt-2 flex justify-end gap-6 text-sm">
                  <div>Total RM Cost: <b className="font-mono">{inr(computedCost.total)}</b></div>
                  <div>Cost / unit: <b className="font-mono">{inr(computedCost.per_unit)}</b></div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={form.status && form.status !== "draft"} />
              </div>

              {form.id && form.status !== "draft" && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
                  <GitBranch className="w-3 h-3 inline mr-1" /> This BOM is <b>{form.status}</b> and cannot be edited. Use <b>Clone</b> to create a new draft version.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            {form && (!form.status || form.status === "draft") && (
              <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="bom-form-save">Save draft</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
